import { useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AmbientBackground from '../components/ui/AmbientBackground';
import { useTheme } from '../contexts/ThemeContext';
import CareerlyLogo from '../assets/CareerlyLogo.png';

// Company logos
import googleLogo from '../assets/company_logos/logos/white_google.png';
import metaLogo from '../assets/company_logos/logos/white_meta.png';
import microsoftLogo from '../assets/company_logos/logos/white_microsoft.png';
import appleLogo from '../assets/company_logos/logos/white_apple.png';
import amazonLogo from '../assets/company_logos/logos/white_amazon.png';
import nvidiaLogo from '../assets/company_logos/logos/white_nvidia.png';

const Landing = () => {
    const navigate = useNavigate();
    const { lockDarkMode, unlockDarkMode } = useTheme();

    // FORCE dark mode - lock it so it can NEVER be changed to light on this page
    useLayoutEffect(() => {
        lockDarkMode();
        return () => unlockDarkMode(); // Restore user's preference when leaving
    }, [lockDarkMode, unlockDarkMode]);

    const scrollToSection = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };

    const styles = {
        // Navigation
        nav: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            padding: 'var(--space-md) var(--space-xl)',
            background: 'rgba(10, 10, 18, 0.8)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderBottom: '1px solid var(--border-subtle)',
        },
        navInner: {
            maxWidth: '1400px',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
        },
        logo: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            textDecoration: 'none',
            color: 'var(--text-primary)',
        },
        logoMark: {
            width: '36px',
            height: '36px',
        },
        logoText: {
            fontFamily: 'var(--font-display)',
            fontSize: '1.5rem',
            fontWeight: 600,
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
        },
        navLinks: {
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 'var(--space-xl)',
        },
        navLink: {
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            fontSize: '0.95rem',
            fontWeight: 500,
            transition: 'color var(--duration-fast)',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
        },
        navActions: {
            display: 'flex',
            gap: 'var(--space-md)',
        },

        // Buttons
        btn: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-sm)',
            fontFamily: 'var(--font-body)',
            fontWeight: 600,
            fontSize: '0.95rem',
            padding: 'var(--space-sm) var(--space-lg)',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'none',
            transition: 'all var(--duration-base) var(--ease-out)',
        },
        btnPrimary: {
            background: 'var(--gradient-primary)',
            color: 'white',
            boxShadow: 'var(--shadow-sm), var(--glow-indigo)',
        },
        btnSecondary: {
            background: 'var(--bg-surface)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-light)',
        },
        btnGhost: {
            background: 'transparent',
            color: 'var(--text-secondary)',
        },
        btnLarge: {
            padding: 'var(--space-md) var(--space-2xl)',
            fontSize: '1.05rem',
        },
        btnFull: {
            width: '100%',
        },

        // Hero
        hero: {
            position: 'relative',
            zIndex: 1,
            minHeight: '100vh',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'var(--space-4xl)',
            alignItems: 'center',
            padding: '8rem var(--space-xl) var(--space-4xl)',
            maxWidth: '1400px',
            margin: '0 auto',
        },
        heroContent: {
            maxWidth: '640px',
        },
        heroBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            padding: 'var(--space-sm) var(--space-lg)',
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            borderRadius: 'var(--radius-full)',
            fontSize: '0.875rem',
            color: 'var(--amber-400)',
            marginBottom: 'var(--space-xl)',
            animation: 'badge-float 3s ease-in-out infinite',
        },
        heroTitle: {
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: 700,
            lineHeight: 1.1,
            marginBottom: 'var(--space-xl)',
        },
        titleLine: {
            display: 'block',
            color: 'var(--text-primary)',
        },
        titleGradient: {
            display: 'block',
            background: 'linear-gradient(135deg, var(--indigo-400) 0%, var(--cyan-300) 50%, var(--amber-400) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundSize: '200% auto',
            animation: 'gradient-shift 8s linear infinite',
        },
        heroSubtitle: {
            fontSize: '1.2rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.7,
            marginBottom: 'var(--space-2xl)',
        },
        heroCta: {
            display: 'flex',
            gap: 'var(--space-lg)',
            marginBottom: 'var(--space-3xl)',
        },

        // Hero Visual
        heroVisual: {
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        dashboardPreview: {
            width: '100%',
            maxWidth: '500px',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-xl)',
            overflow: 'hidden',
            boxShadow: 'var(--shadow-lg), var(--glow-indigo)',
            animation: 'preview-float 6s ease-in-out infinite',
        },
        previewHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-md)',
            padding: 'var(--space-md) var(--space-lg)',
            background: 'var(--bg-surface)',
            borderBottom: '1px solid var(--border-subtle)',
        },
        previewDots: {
            display: 'flex',
            gap: '6px',
        },
        previewDot: {
            width: '10px',
            height: '10px',
            borderRadius: '50%',
        },
        previewTitle: {
            fontSize: '0.875rem',
            color: 'var(--text-tertiary)',
        },
        previewContent: {
            display: 'flex',
            padding: 'var(--space-lg)',
            gap: 'var(--space-lg)',
            minHeight: '300px',
        },
        previewSidebar: {
            width: '60px',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-sm)',
        },
        previewNavItem: {
            height: '40px',
            background: 'var(--bg-surface)',
            borderRadius: 'var(--radius-sm)',
        },
        previewNavItemActive: {
            height: '40px',
            background: 'var(--gradient-primary)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--glow-indigo)',
        },
        previewMain: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-lg)',
        },
        previewWidget: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-md)',
            padding: 'var(--space-md) var(--space-lg)',
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(239, 68, 68, 0.1))',
            borderRadius: 'var(--radius-md)',
            border: '1px solid rgba(245, 158, 11, 0.2)',
        },
        widgetIcon: {
            fontSize: '1.5rem',
        },
        widgetNumber: {
            fontFamily: 'var(--font-display)',
            fontSize: '1.5rem',
            fontWeight: 700,
            color: 'var(--amber-400)',
        },
        widgetLabel: {
            fontSize: '0.75rem',
            color: 'var(--text-tertiary)',
        },
        previewCards: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 'var(--space-md)',
        },
        previewCard: {
            height: '80px',
            background: 'var(--bg-surface)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-subtle)',
        },

        // Floating Badges
        floatingBadge: {
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            padding: 'var(--space-sm) var(--space-md)',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-full)',
            fontSize: '0.875rem',
            fontWeight: 500,
            boxShadow: 'var(--shadow-md)',
        },
        badge1: {
            top: '20%',
            right: '-10%',
            animation: 'float-badge 4s ease-in-out infinite',
        },
        badge2: {
            bottom: '30%',
            left: '-5%',
            animation: 'float-badge 4s ease-in-out infinite',
            animationDelay: '-1.5s',
        },
        badge3: {
            top: '60%',
            right: '-15%',
            animation: 'float-badge 4s ease-in-out infinite',
            animationDelay: '-3s',
        },

        // Section styling
        section: {
            position: 'relative',
            zIndex: 1,
            padding: 'var(--space-4xl) var(--space-xl)',
        },
        sectionHeader: {
            textAlign: 'center',
            marginBottom: 'var(--space-4xl)',
            maxWidth: '700px',
            marginLeft: 'auto',
            marginRight: 'auto',
        },
        sectionEyebrow: {
            display: 'inline-block',
            fontSize: '0.875rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: 'var(--cyan-400)',
            marginBottom: 'var(--space-md)',
        },
        sectionTitle: {
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 700,
            lineHeight: 1.2,
            marginBottom: 'var(--space-lg)',
            color: 'var(--text-primary)',
        },
        sectionSubtitle: {
            fontSize: '1.1rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.7,
        },
        gradientText: {
            background: 'linear-gradient(135deg, var(--indigo-400) 0%, var(--cyan-300) 50%, var(--amber-400) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundSize: '200% auto',
            animation: 'gradient-shift 8s linear infinite',
        },

        // Features
        featuresContainer: {
            maxWidth: '1400px',
            margin: '0 auto',
        },
        featuresGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'var(--space-xl)',
        },
        featureCard: {
            position: 'relative',
            padding: 'var(--space-2xl)',
            background: 'var(--bg-card)',
            backdropFilter: 'blur(20px)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-xl)',
            transition: 'all var(--duration-base) var(--ease-out)',
            overflow: 'hidden',
        },
        featureIcon: {
            width: '64px',
            height: '64px',
            marginBottom: 'var(--space-lg)',
        },
        featureTitle: {
            fontFamily: 'var(--font-display)',
            fontSize: '1.5rem',
            fontWeight: 600,
            marginBottom: 'var(--space-md)',
            color: 'var(--text-primary)',
        },
        featureDesc: {
            color: 'var(--text-secondary)',
            lineHeight: 1.7,
            marginBottom: 'var(--space-lg)',
        },
        featureHighlights: {
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-sm)',
            padding: 0,
            margin: 0,
        },
        featureHighlightItem: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            fontSize: '0.9rem',
            color: 'var(--text-secondary)',
        },
        highlightDot: {
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: 'var(--gradient-primary)',
            flexShrink: 0,
        },

        // How It Works
        howItWorksContainer: {
            maxWidth: '900px',
            margin: '0 auto',
        },
        stepsContainer: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3xl)',
        },
        stepsLine: {
            position: 'absolute',
            left: '32px',
            top: 0,
            bottom: 0,
            width: '2px',
            background: 'linear-gradient(to bottom, var(--indigo-500), var(--cyan-400), var(--amber-500))',
            opacity: 0.3,
        },
        step: {
            display: 'flex',
            gap: 'var(--space-xl)',
            alignItems: 'flex-start',
        },
        stepNumber: {
            position: 'relative',
            width: '64px',
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--bg-elevated)',
            border: '2px solid var(--border-medium)',
            borderRadius: 'var(--radius-lg)',
            flexShrink: 0,
            zIndex: 1,
        },
        stepNumberText: {
            fontFamily: 'var(--font-display)',
            fontSize: '1.25rem',
            fontWeight: 700,
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
        },
        stepContent: {
            paddingTop: 'var(--space-md)',
        },
        stepTitle: {
            fontFamily: 'var(--font-display)',
            fontSize: '1.5rem',
            fontWeight: 600,
            marginBottom: 'var(--space-sm)',
            color: 'var(--text-primary)',
        },
        stepDesc: {
            color: 'var(--text-secondary)',
            lineHeight: 1.7,
        },

        // Social Proof
        socialProofSection: {
            background: 'var(--bg-base)',
        },
        proofContent: {
            maxWidth: '1000px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr',
            gap: 'var(--space-4xl)',
            alignItems: 'center',
        },
        testimonialCard: {
            padding: 'var(--space-2xl)',
            background: 'var(--bg-card)',
            backdropFilter: 'blur(20px)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-xl)',
            position: 'relative',
        },
        quoteMark: {
            position: 'absolute',
            top: 'var(--space-lg)',
            left: 'var(--space-xl)',
            fontFamily: 'var(--font-display)',
            fontSize: '6rem',
            lineHeight: 1,
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            opacity: 0.3,
        },
        testimonialText: {
            fontSize: '1.15rem',
            lineHeight: 1.8,
            color: 'var(--text-secondary)',
            marginBottom: 'var(--space-xl)',
            position: 'relative',
            zIndex: 1,
        },
        testimonialAuthor: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-md)',
        },
        authorAvatar: {
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'var(--gradient-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: '0.875rem',
            color: 'white',
        },
        authorInfo: {
            display: 'flex',
            flexDirection: 'column',
        },
        authorName: {
            fontWeight: 600,
            color: 'var(--text-primary)',
        },
        authorTitle: {
            fontSize: '0.875rem',
            color: 'var(--text-tertiary)',
        },
        proofLogos: {
            textAlign: 'center',
        },
        proofLabel: {
            display: 'block',
            fontSize: '0.875rem',
            color: 'var(--text-tertiary)',
            marginBottom: 'var(--space-xl)',
        },
        logosGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 'var(--space-lg)',
        },
        companyLogo: {
            padding: 'var(--space-md) var(--space-lg)',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '60px',
        },
        companyLogoImg: {
            maxHeight: '24px',
            maxWidth: '100%',
            objectFit: 'contain',
            opacity: 0.7,
        },

        // Pricing
        pricingContainer: {
            maxWidth: '1000px',
            margin: '0 auto',
        },
        pricingGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'var(--space-xl)',
            alignItems: 'start',
        },
        pricingCard: {
            padding: 'var(--space-2xl)',
            background: 'var(--bg-card)',
            backdropFilter: 'blur(20px)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-xl)',
            transition: 'all var(--duration-base) var(--ease-out)',
            position: 'relative',
        },
        pricingCardFeatured: {
            borderColor: 'var(--indigo-500)',
            boxShadow: 'var(--glow-indigo)',
        },
        featuredBadge: {
            position: 'absolute',
            top: '-12px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: 'var(--space-xs) var(--space-lg)',
            background: 'var(--gradient-primary)',
            borderRadius: 'var(--radius-full)',
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'white',
        },
        pricingHeader: {
            textAlign: 'center',
            paddingBottom: 'var(--space-xl)',
            borderBottom: '1px solid var(--border-subtle)',
            marginBottom: 'var(--space-xl)',
        },
        planName: {
            fontFamily: 'var(--font-display)',
            fontSize: '1.25rem',
            fontWeight: 600,
            marginBottom: 'var(--space-md)',
            color: 'var(--text-primary)',
        },
        planPrice: {
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'center',
            gap: '2px',
            marginBottom: 'var(--space-sm)',
        },
        priceCurrency: {
            fontSize: '1.5rem',
            color: 'var(--text-tertiary)',
        },
        priceAmount: {
            fontFamily: 'var(--font-display)',
            fontSize: '3.5rem',
            fontWeight: 700,
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
        },
        pricePeriod: {
            fontSize: '1rem',
            color: 'var(--text-tertiary)',
        },
        planDesc: {
            fontSize: '0.9rem',
            color: 'var(--text-tertiary)',
        },
        planFeatures: {
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-md)',
            padding: 0,
            margin: 0,
            marginBottom: 'var(--space-xl)',
        },
        planFeatureItem: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-md)',
            color: 'var(--text-secondary)',
        },

        // Final CTA
        finalCta: {
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
        },
        ctaContent: {
            position: 'relative',
            zIndex: 1,
        },
        ctaTitle: {
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 700,
            lineHeight: 1.2,
            marginBottom: 'var(--space-lg)',
            color: 'var(--text-primary)',
        },
        ctaSubtitle: {
            fontSize: '1.1rem',
            color: 'var(--text-secondary)',
            marginBottom: 'var(--space-2xl)',
            maxWidth: '500px',
            marginLeft: 'auto',
            marginRight: 'auto',
        },
        ctaNote: {
            marginTop: 'var(--space-lg)',
            fontSize: '0.875rem',
            color: 'var(--text-tertiary)',
        },
        ctaGlow: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '600px',
            height: '400px',
            background: 'radial-gradient(ellipse, rgba(99, 102, 241, 0.2), transparent 60%)',
            filter: 'blur(60px)',
            pointerEvents: 'none',
        },

        // Footer
        footer: {
            position: 'relative',
            zIndex: 1,
            borderTop: '1px solid var(--border-subtle)',
            padding: 'var(--space-4xl) var(--space-xl) var(--space-xl)',
        },
        footerInner: {
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 'var(--space-3xl)',
        },
        footerTagline: {
            color: 'var(--text-tertiary)',
            marginTop: 'var(--space-sm)',
            fontSize: '0.9rem',
        },
        footerLinks: {
            display: 'flex',
            gap: 'var(--space-4xl)',
        },
        footerCol: {
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-sm)',
        },
        footerColTitle: {
            fontSize: '0.875rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--text-tertiary)',
            marginBottom: 'var(--space-sm)',
        },
        footerLink: {
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            fontSize: '0.9rem',
            transition: 'color var(--duration-fast)',
        },
        footerBottom: {
            maxWidth: '1200px',
            margin: '0 auto',
            paddingTop: 'var(--space-xl)',
            borderTop: '1px solid var(--border-subtle)',
            textAlign: 'center',
        },
        footerCopyright: {
            fontSize: '0.875rem',
            color: 'var(--text-muted)',
        },
    };

    // Keyframe animations - using translate3d for GPU acceleration
    const keyframes = `
        @keyframes badge-float {
            0%, 100% { transform: translate3d(0, 0, 0); }
            50% { transform: translate3d(0, -4px, 0); }
        }
        @keyframes gradient-shift {
            0% { background-position: 0% center; }
            100% { background-position: 200% center; }
        }
        @keyframes preview-float {
            0%, 100% { transform: translate3d(0, 0, 0) rotate(0deg); }
            50% { transform: translate3d(0, -10px, 0) rotate(1deg); }
        }
        @keyframes float-badge {
            0%, 100% { transform: translate3d(0, 0, 0) rotate(-2deg); }
            50% { transform: translate3d(0, -15px, 0) rotate(2deg); }
        }
    `;

    const CheckIcon = () => (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M6 10l3 3 5-6" stroke="var(--cyan-400)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );

    return (
        <div style={{
            overflowX: 'hidden',
            width: '100%',
            minHeight: '100vh',
            background: 'var(--bg-base)',
        }}>
            <style>{keyframes}</style>
            <AmbientBackground variant="landing" />

            {/* Navigation */}
            <nav style={styles.nav}>
                <div style={styles.navInner}>
                    <a href="/" style={styles.logo}>
                        <img
                            src={CareerlyLogo}
                            alt="Careerly"
                            style={{
                                height: '40px',
                                width: 'auto',
                                objectFit: 'contain'
                            }}
                        />
                    </a>

                    <div style={styles.navLinks}>
                        <button style={styles.navLink} onClick={() => scrollToSection('features')}>Features</button>
                        <button style={styles.navLink} onClick={() => scrollToSection('how-it-works')}>How It Works</button>
                        <button style={styles.navLink} onClick={() => scrollToSection('pricing')}>Pricing</button>
                    </div>

                    <div style={styles.navActions}>
                        <button style={{ ...styles.btn, ...styles.btnGhost }} onClick={() => navigate('/login')}>Sign In</button>
                        <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => navigate('/login')}>
                            <span>Get Started Free</span>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section style={styles.hero}>
                <div style={styles.heroContent}>
                    <h1 style={styles.heroTitle}>
                        <span style={styles.titleLine}>Your Career Journey,</span>
                        <span style={styles.titleGradient}>Supercharged by AI</span>
                    </h1>

                    <p style={styles.heroSubtitle}>
                        Stop juggling spreadsheets, ChatGPT prompts, and random interview tips.
                        Careerly brings AI-powered job discovery, instant resume generation, and
                        interview prep into one seamless experience.
                    </p>

                    <div style={styles.heroCta}>
                        <button
                            style={{ ...styles.btn, ...styles.btnPrimary, ...styles.btnLarge }}
                            onClick={() => navigate('/login')}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = 'var(--shadow-md), 0 0 50px rgba(99, 102, 241, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'var(--shadow-sm), var(--glow-indigo)';
                            }}
                        >
                            <span>Start Your Journey</span>
                        </button>
                        <button
                            style={{ ...styles.btn, ...styles.btnSecondary, ...styles.btnLarge }}
                            onClick={() => scrollToSection('how-it-works')}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.background = 'var(--bg-elevated)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.background = 'var(--bg-surface)';
                            }}
                        >
                            <span>Learn More</span>
                        </button>
                    </div>

                </div>

                <div style={styles.heroVisual}>
                    <div style={styles.dashboardPreview}>
                        <div style={styles.previewHeader}>
                            <div style={styles.previewDots}>
                                <span style={{ ...styles.previewDot, background: '#ff5f57' }}></span>
                                <span style={{ ...styles.previewDot, background: '#febc2e' }}></span>
                                <span style={{ ...styles.previewDot, background: '#28c840' }}></span>
                            </div>
                            <span style={styles.previewTitle}>Dashboard</span>
                        </div>
                        <div style={styles.previewContent}>
                            <div style={styles.previewSidebar}>
                                <div style={styles.previewNavItemActive}></div>
                                <div style={styles.previewNavItem}></div>
                                <div style={styles.previewNavItem}></div>
                                <div style={styles.previewNavItem}></div>
                            </div>
                            <div style={styles.previewMain}>
                                <div style={styles.previewWidget}>
                                    <div style={styles.widgetIcon}>🔥</div>
                                    <div>
                                        <div style={styles.widgetNumber}>12</div>
                                        <div style={styles.widgetLabel}>Day Streak</div>
                                    </div>
                                </div>
                                <div style={styles.previewCards}>
                                    <div style={styles.previewCard}></div>
                                    <div style={styles.previewCard}></div>
                                    <div style={styles.previewCard}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ ...styles.floatingBadge, ...styles.badge1 }}>
                        <span>Resume Ready!</span>
                    </div>
                    <div style={{ ...styles.floatingBadge, ...styles.badge2 }}>
                        <span>+250 XP</span>
                    </div>
                    <div style={{ ...styles.floatingBadge, ...styles.badge3 }}>
                        <span>5 New Matches</span>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section style={styles.section} id="features">
                <div style={styles.featuresContainer}>
                    <div style={styles.sectionHeader}>
                        <span style={styles.sectionEyebrow}>The Complete Toolkit</span>
                        <h2 style={styles.sectionTitle}>Everything You Need to<br/><span style={styles.gradientText}>Land the Offer</span></h2>
                        <p style={styles.sectionSubtitle}>Four powerful pillars working together to transform your job search from chaos into confidence.</p>
                    </div>

                    <div style={styles.featuresGrid}>
                        {/* Discover Jobs */}
                        <div
                            style={styles.featureCard}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.borderColor = 'var(--border-medium)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.borderColor = 'var(--border-subtle)';
                            }}
                        >
                            <div style={styles.featureIcon}>
                                <svg viewBox="0 0 48 48" fill="none" style={{ width: '100%', height: '100%' }}>
                                    <circle cx="20" cy="20" r="14" stroke="url(#discover-grad)" strokeWidth="3"/>
                                    <path d="M30 30l12 12" stroke="url(#discover-grad)" strokeWidth="3" strokeLinecap="round"/>
                                    <circle cx="20" cy="20" r="6" fill="url(#discover-fill)" opacity="0.3"/>
                                    <defs>
                                        <linearGradient id="discover-grad" x1="6" y1="6" x2="42" y2="42">
                                            <stop offset="0%" stopColor="#6366F1"/>
                                            <stop offset="100%" stopColor="#22D3EE"/>
                                        </linearGradient>
                                        <linearGradient id="discover-fill" x1="14" y1="14" x2="26" y2="26">
                                            <stop offset="0%" stopColor="#6366F1"/>
                                            <stop offset="100%" stopColor="#22D3EE"/>
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </div>
                            <h3 style={styles.featureTitle}>Discover Jobs</h3>
                            <p style={styles.featureDesc}>Smart job matching that actually understands your skills. Filter noise, save opportunities, and generate tailored resumes with one click.</p>
                            <ul style={styles.featureHighlights}>
                                <li style={styles.featureHighlightItem}><span style={styles.highlightDot}></span>AI-Powered Matching</li>
                                <li style={styles.featureHighlightItem}><span style={styles.highlightDot}></span>One-Click Resume Generation</li>
                                <li style={styles.featureHighlightItem}><span style={styles.highlightDot}></span>Smart Filtering & Hiding</li>
                            </ul>
                        </div>

                        {/* Generate Resumes */}
                        <div
                            style={styles.featureCard}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.borderColor = 'var(--border-medium)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.borderColor = 'var(--border-subtle)';
                            }}
                        >
                            <div style={styles.featureIcon}>
                                <svg viewBox="0 0 48 48" fill="none" style={{ width: '100%', height: '100%' }}>
                                    <rect x="8" y="4" width="24" height="32" rx="2" stroke="url(#resume-grad)" strokeWidth="3"/>
                                    <path d="M14 12h12M14 18h12M14 24h8" stroke="url(#resume-grad)" strokeWidth="2" strokeLinecap="round"/>
                                    <circle cx="34" cy="34" r="10" fill="url(#resume-fill)"/>
                                    <path d="M31 34l2 2 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <defs>
                                        <linearGradient id="resume-grad" x1="8" y1="4" x2="40" y2="44">
                                            <stop offset="0%" stopColor="#22D3EE"/>
                                            <stop offset="100%" stopColor="#6366F1"/>
                                        </linearGradient>
                                        <linearGradient id="resume-fill" x1="24" y1="24" x2="44" y2="44">
                                            <stop offset="0%" stopColor="#6366F1"/>
                                            <stop offset="100%" stopColor="#22D3EE"/>
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </div>
                            <h3 style={styles.featureTitle}>Generate Resumes</h3>
                            <p style={styles.featureDesc}>Upload once, generate forever. Our AI crafts ATS-optimized resumes tailored to each job in under 10 seconds.</p>
                            <ul style={styles.featureHighlights}>
                                <li style={styles.featureHighlightItem}><span style={styles.highlightDot}></span>10-Second Generation</li>
                                <li style={styles.featureHighlightItem}><span style={styles.highlightDot}></span>ATS-Optimized Output</li>
                                <li style={styles.featureHighlightItem}><span style={styles.highlightDot}></span>AI Co-Pilot Assistance</li>
                            </ul>
                        </div>

                        {/* Interview Prep */}
                        <div
                            style={styles.featureCard}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.borderColor = 'var(--border-medium)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.borderColor = 'var(--border-subtle)';
                            }}
                        >
                            <div style={styles.featureIcon}>
                                <svg viewBox="0 0 48 48" fill="none" style={{ width: '100%', height: '100%' }}>
                                    <path d="M40 22c0-8.837-7.163-16-16-16S8 13.163 8 22c0 5.105 2.392 9.647 6.118 12.583L12 44l6.5-4 6.5 4 6.5-4 6.5 4-2.118-9.417C39.608 31.647 40 27.105 40 22z" stroke="url(#interview-grad)" strokeWidth="3"/>
                                    <circle cx="18" cy="20" r="2" fill="url(#interview-grad)"/>
                                    <circle cx="30" cy="20" r="2" fill="url(#interview-grad)"/>
                                    <path d="M20 27c1.5 2 4.5 2 6 0" stroke="url(#interview-grad)" strokeWidth="2" strokeLinecap="round"/>
                                    <defs>
                                        <linearGradient id="interview-grad" x1="8" y1="6" x2="40" y2="44">
                                            <stop offset="0%" stopColor="#F59E0B"/>
                                            <stop offset="100%" stopColor="#6366F1"/>
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </div>
                            <h3 style={styles.featureTitle}>Interview Prep</h3>
                            <p style={styles.featureDesc}>Walk in confident. AI-generated STAR stories, company research, resume walkthroughs, and realistic mock interviews with feedback.</p>
                            <ul style={styles.featureHighlights}>
                                <li style={styles.featureHighlightItem}><span style={styles.highlightDot}></span>STAR Story Generator</li>
                                <li style={styles.featureHighlightItem}><span style={styles.highlightDot}></span>Company Deep Dives</li>
                                <li style={styles.featureHighlightItem}><span style={styles.highlightDot}></span>AI Mock Interviews</li>
                            </ul>
                        </div>

                        {/* Quiz Practice */}
                        <div
                            style={styles.featureCard}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.borderColor = 'var(--border-medium)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.borderColor = 'var(--border-subtle)';
                            }}
                        >
                            <div style={styles.featureIcon}>
                                <svg viewBox="0 0 48 48" fill="none" style={{ width: '100%', height: '100%' }}>
                                    <path d="M24 4l4 8 8 1-6 5.5 1.5 8.5-7.5-4-7.5 4 1.5-8.5-6-5.5 8-1 4-8z" stroke="url(#skills-grad)" strokeWidth="3" strokeLinejoin="round"/>
                                    <circle cx="24" cy="36" r="8" stroke="url(#skills-grad)" strokeWidth="3"/>
                                    <path d="M21 36l2 2 4-4" stroke="url(#skills-grad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <defs>
                                        <linearGradient id="skills-grad" x1="10" y1="4" x2="38" y2="44">
                                            <stop offset="0%" stopColor="#22D3EE"/>
                                            <stop offset="100%" stopColor="#F59E0B"/>
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </div>
                            <h3 style={styles.featureTitle}>Quiz Practice</h3>
                            <p style={styles.featureDesc}>Keep your skills sharp with adaptive quizzes. Study mode, challenge mode, and daily practice to level up your expertise.</p>
                            <ul style={styles.featureHighlights}>
                                <li style={styles.featureHighlightItem}><span style={styles.highlightDot}></span>Adaptive Learning</li>
                                <li style={styles.featureHighlightItem}><span style={styles.highlightDot}></span>Daily Challenges</li>
                                <li style={styles.featureHighlightItem}><span style={styles.highlightDot}></span>Progress Tracking</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section style={styles.section} id="how-it-works">
                <div style={styles.howItWorksContainer}>
                    <div style={styles.sectionHeader}>
                        <span style={styles.sectionEyebrow}>Your Path to Success</span>
                        <h2 style={styles.sectionTitle}>Three Steps to<br/><span style={styles.gradientText}>Career Momentum</span></h2>
                    </div>

                    <div style={styles.stepsContainer}>
                        <div style={styles.stepsLine}></div>

                        <div style={styles.step}>
                            <div style={styles.stepNumber}>
                                <span style={styles.stepNumberText}>01</span>
                            </div>
                            <div style={styles.stepContent}>
                                <h3 style={styles.stepTitle}>Set Your Profile</h3>
                                <p style={styles.stepDesc}>Upload your resume and tell us your goals. Our AI learns your unique strengths and career aspirations.</p>
                            </div>
                        </div>

                        <div style={styles.step}>
                            <div style={styles.stepNumber}>
                                <span style={styles.stepNumberText}>02</span>
                            </div>
                            <div style={styles.stepContent}>
                                <h3 style={styles.stepTitle}>Discover & Apply</h3>
                                <p style={styles.stepDesc}>Browse AI-matched jobs, generate tailored resumes instantly, and track every application in one place.</p>
                            </div>
                        </div>

                        <div style={styles.step}>
                            <div style={styles.stepNumber}>
                                <span style={styles.stepNumberText}>03</span>
                            </div>
                            <div style={styles.stepContent}>
                                <h3 style={styles.stepTitle}>Prepare & Succeed</h3>
                                <p style={styles.stepDesc}>Ace your interviews with AI-powered prep, mock sessions, and personalized coaching. Land that offer.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Social Proof Section */}
            <section style={{ ...styles.section, ...styles.socialProofSection }}>
                <div style={styles.proofContent}>
                    <div style={styles.testimonialCard}>
                        <span style={styles.quoteMark}>"</span>
                        <p style={styles.testimonialText}>
                            I built Careerly because I was frustrated with how scattered job search tools were.
                            After using my own platform throughout my search, I landed a Software Engineer offer at JPMorgan.
                            Now I want to help other students do the same.
                        </p>
                        <div style={styles.testimonialAuthor}>
                            <div style={styles.authorAvatar}>PJ</div>
                            <div style={styles.authorInfo}>
                                <span style={styles.authorName}>PJ</span>
                                <span style={styles.authorTitle}>Founder & Computer Engineering Senior at Pitt</span>
                            </div>
                        </div>
                    </div>

                    <div style={styles.proofLogos}>
                        <span style={styles.proofLabel}>Users have landed roles at</span>
                        <div style={styles.logosGrid}>
                            <div style={styles.companyLogo}><img src={googleLogo} alt="Google" style={styles.companyLogoImg} /></div>
                            <div style={styles.companyLogo}><img src={metaLogo} alt="Meta" style={styles.companyLogoImg} /></div>
                            <div style={styles.companyLogo}><img src={amazonLogo} alt="Amazon" style={styles.companyLogoImg} /></div>
                            <div style={styles.companyLogo}><img src={microsoftLogo} alt="Microsoft" style={styles.companyLogoImg} /></div>
                            <div style={styles.companyLogo}><img src={appleLogo} alt="Apple" style={styles.companyLogoImg} /></div>
                            <div style={styles.companyLogo}><img src={nvidiaLogo} alt="Nvidia" style={styles.companyLogoImg} /></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section style={styles.section} id="pricing">
                <div style={styles.pricingContainer}>
                    <div style={styles.sectionHeader}>
                        <span style={styles.sectionEyebrow}>Simple Pricing</span>
                        <h2 style={styles.sectionTitle}>Invest in Your<br/><span style={styles.gradientText}>Career Future</span></h2>
                        <p style={styles.sectionSubtitle}>Start free. Upgrade when you're ready to go all-in.</p>
                    </div>

                    <div style={styles.pricingGrid}>
                        {/* Starter Plan */}
                        <div
                            style={styles.pricingCard}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.borderColor = 'var(--border-medium)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.borderColor = 'var(--border-subtle)';
                            }}
                        >
                            <div style={styles.pricingHeader}>
                                <h3 style={styles.planName}>Starter</h3>
                                <div style={styles.planPrice}>
                                    <span style={styles.priceAmount}>Free</span>
                                </div>
                                <p style={styles.planDesc}>Perfect for exploring the platform</p>
                            </div>
                            <ul style={styles.planFeatures}>
                                <li style={styles.planFeatureItem}><CheckIcon /><span>5 AI Resume Generations</span></li>
                                <li style={styles.planFeatureItem}><CheckIcon /><span>3 Mock Interviews</span></li>
                                <li style={styles.planFeatureItem}><CheckIcon /><span>Unlimited Job Discovery</span></li>
                                <li style={styles.planFeatureItem}><CheckIcon /><span>Basic Quiz Practice</span></li>
                                <li style={styles.planFeatureItem}><CheckIcon /><span>Progress Tracking</span></li>
                            </ul>
                            <button
                                style={{ ...styles.btn, ...styles.btnSecondary, ...styles.btnFull }}
                                onClick={() => navigate('/login')}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.background = 'var(--bg-elevated)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.background = 'var(--bg-surface)';
                                }}
                            >
                                Get Started Free
                            </button>
                        </div>

                        {/* Pro Plan */}
                        <div
                            style={{ ...styles.pricingCard, ...styles.pricingCardFeatured }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            <div style={styles.featuredBadge}>Most Popular</div>
                            <div style={styles.pricingHeader}>
                                <h3 style={styles.planName}>Pro</h3>
                                <div style={{
                                    fontSize: '1.25rem',
                                    color: 'var(--text-muted)',
                                    textDecoration: 'line-through',
                                    marginBottom: 'var(--space-xs)',
                                }}>
                                    $29.99/month
                                </div>
                                <div style={styles.planPrice}>
                                    <span style={styles.priceCurrency}>$</span>
                                    <span style={styles.priceAmount}>19</span>
                                    <span style={{ ...styles.pricePeriod, fontSize: '1.1rem' }}>.99/month</span>
                                </div>
                                <div style={{
                                    display: 'inline-block',
                                    padding: 'var(--space-xs) var(--space-md)',
                                    background: 'rgba(34, 197, 94, 0.15)',
                                    border: '1px solid rgba(34, 197, 94, 0.3)',
                                    borderRadius: 'var(--radius-full)',
                                    color: '#22c55e',
                                    fontSize: '0.85rem',
                                    fontWeight: 600,
                                    marginBottom: 'var(--space-sm)',
                                }}>
                                    Save 33% - Limited Time!
                                </div>
                                <p style={styles.planDesc}>Everything unlimited. Go all-in.</p>
                            </div>
                            <ul style={styles.planFeatures}>
                                <li style={styles.planFeatureItem}><CheckIcon /><span><strong style={{ color: 'var(--text-primary)' }}>Unlimited</strong> Resume Generations</span></li>
                                <li style={styles.planFeatureItem}><CheckIcon /><span><strong style={{ color: 'var(--text-primary)' }}>Unlimited</strong> Mock Interviews</span></li>
                                <li style={styles.planFeatureItem}><CheckIcon /><span><strong style={{ color: 'var(--text-primary)' }}>Unlimited</strong> STAR Stories</span></li>
                                <li style={styles.planFeatureItem}><CheckIcon /><span>Company Research Deep Dives</span></li>
                                <li style={styles.planFeatureItem}><CheckIcon /><span>Advanced Analytics Dashboard</span></li>
                                <li style={styles.planFeatureItem}><CheckIcon /><span>Priority Support</span></li>
                            </ul>
                            <button
                                style={{ ...styles.btn, ...styles.btnPrimary, ...styles.btnFull }}
                                onClick={() => navigate('/login')}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = 'var(--shadow-md), 0 0 50px rgba(99, 102, 241, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'var(--shadow-sm), var(--glow-indigo)';
                                }}
                            >
                                Upgrade to Pro
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section style={{ ...styles.section, ...styles.finalCta }}>
                <div style={styles.ctaGlow}></div>
                <div style={styles.ctaContent}>
                    <h2 style={styles.ctaTitle}>Ready to Land Your<br/><span style={styles.gradientText}>Dream Career?</span></h2>
                    <p style={styles.ctaSubtitle}>Join thousands of job seekers who transformed their career journey with Careerly.</p>
                    <button
                        style={{ ...styles.btn, ...styles.btnPrimary, ...styles.btnLarge }}
                        onClick={() => navigate('/login')}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = 'var(--shadow-md), 0 0 50px rgba(99, 102, 241, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'var(--shadow-sm), var(--glow-indigo)';
                        }}
                    >
                        <span>Get Started Free</span>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M4 10h12M12 6l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                    <p style={styles.ctaNote}>No credit card required • Free forever on Starter plan</p>
                </div>
            </section>

            {/* Footer */}
            <footer style={styles.footer}>
                <div style={styles.footerInner}>
                    <div>
                        <a href="/" style={styles.logo}>
                            <img
                                src={CareerlyLogo}
                                alt="Careerly"
                                style={{
                                    height: '36px',
                                    width: 'auto',
                                    objectFit: 'contain'
                                }}
                            />
                        </a>
                        <p style={styles.footerTagline}>Your career journey, supercharged.</p>
                    </div>
                    <div style={styles.footerLinks}>
                        <div style={styles.footerCol}>
                            <h4 style={styles.footerColTitle}>Product</h4>
                            <button style={{ ...styles.footerLink, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }} onClick={() => scrollToSection('features')}>Features</button>
                            <button style={{ ...styles.footerLink, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }} onClick={() => scrollToSection('pricing')}>Pricing</button>
                            <button style={{ ...styles.footerLink, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }} onClick={() => navigate('/login')}>Dashboard</button>
                        </div>
                        <div style={styles.footerCol}>
                            <h4 style={styles.footerColTitle}>Company</h4>
                            <span style={styles.footerLink}>About</span>
                            <span style={styles.footerLink}>Blog</span>
                            <span style={styles.footerLink}>Careers</span>
                        </div>
                        <div style={styles.footerCol}>
                            <h4 style={styles.footerColTitle}>Legal</h4>
                            <span style={styles.footerLink}>Privacy</span>
                            <span style={styles.footerLink}>Terms</span>
                        </div>
                    </div>
                </div>
                <div style={styles.footerBottom}>
                    <p style={styles.footerCopyright}>© 2025 Careerly. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

export default Landing;
